// ========================================
// ADD-ON UPSELL MODULE - OPTIMIZED
// Supports: Standard products, Customily app, and Personalization apps
// ========================================

/**
 * Configuration
 */
const CONFIG = {
  selectors: {
    wrapper: ".gift-box-wrapper",
    checkboxes: ".gift-box .upsell-checkbox",
    form: 'form[action="/cart/add"]',
    customilyApp: ".customily-main-app1",
  },
  events: {
    personalizationStart: "personalize-start-add-to-cart",
  },
  endpoints: {
    cartAdd: "/cart/add.js",
  },
  redirectUrl: "/cart",
};

/**
 * State management
 */
const state = {
  selectedAddons: new Set(),
  isProcessing: false,
};

/**
 * Initialize module on DOM ready
 */
document.addEventListener("DOMContentLoaded", () => {
  const wrapper = document.querySelector(CONFIG.selectors.wrapper);
  if (!wrapper) return;

  initializeAddonSelection();
  setupEventHandlers();
});

/**
 * Initialize addon checkbox selection tracking
 */
const initializeAddonSelection = () => {
  const checkboxes = document.querySelectorAll(CONFIG.selectors.checkboxes);

  checkboxes.forEach((checkbox) => {
    const variantId = parseInt(checkbox.dataset.variantId);
    if (isNaN(variantId)) return;

    // Track initially checked addons
    if (checkbox.checked) {
      state.selectedAddons.add(variantId);
    }

    // Listen for changes
    checkbox.addEventListener("change", () => {
      if (checkbox.checked) {
        state.selectedAddons.add(variantId);
      } else {
        state.selectedAddons.delete(variantId);
      }
    });
  });
};

/**
 * Setup all event handlers
 */
const setupEventHandlers = () => {
  setupPersonalizationEventHandler();
  setupFormSubmitHandler();
};

/**
 * Setup handler for personalization app event
 */
const setupPersonalizationEventHandler = () => {
  document.addEventListener(CONFIG.events.personalizationStart, async () => {
    if (state.selectedAddons.size === 0 || state.isProcessing) return;

    state.isProcessing = true;

    try {
      await addToCart({ items: getAddonItems() });
    } catch (error) {
      console.error("Failed to add addons:", error);
    } finally {
      state.isProcessing = false;
    }
  });
};

/**
 * Setup handler for standard form submission
 */
const setupFormSubmitHandler = () => {
  const form = document.querySelector(CONFIG.selectors.form);
  if (!form) return;

  form.addEventListener("submit", async (event) => {
    const hasCustomily = isCustomilyActive();

    if (hasCustomily) {
      handleCustomilyFlow();
    } else {
      await handleStandardFlow(event, form);
    }
  });
};

/**
 * Handle Customily app flow
 */
const handleCustomilyFlow = () => {
  if (state.selectedAddons.size === 0) return;
  if (typeof window.customilyShopify === "undefined") return;

  ensureCustomilyHookStructure();

  window.customilyShopify.hooks.cart.onItemAdded = async () => {
    return new Promise(async (resolve) => {
      try {
        await addToCart({ items: getAddonItems() });
      } catch (error) {
        console.error("Failed to add addons:", error);
      } finally {
        resolve(true);
      }
    });
  };
};

/**
 * Handle standard product flow
 */
const handleStandardFlow = async (event, form) => {
  if (state.selectedAddons.size === 0) return;
  if (state.isProcessing) return;

  event.preventDefault();
  state.isProcessing = true;

  try {
    // Add addons first
    await addToCart({ items: getAddonItems() });

    // Add main product
    await addToCart(new FormData(form));

    // Redirect to cart
    window.location.href = CONFIG.redirectUrl;
  } catch (error) {
    console.error("Cart error:", error);
  } finally {
    state.isProcessing = false;
  }
};

/**
 * Check if Customily app is active
 * @returns {boolean}
 */
const isCustomilyActive = () => {
  return document.querySelector(CONFIG.selectors.customilyApp) !== null;
};

/**
 * Ensure Customily hook structure exists
 */
const ensureCustomilyHookStructure = () => {
  if (!window.customilyShopify.hooks) {
    window.customilyShopify.hooks = {};
  }
  if (!window.customilyShopify.hooks.cart) {
    window.customilyShopify.hooks.cart = {};
  }
};

/**
 * Get addon items in Shopify format
 * @returns {Array} Array of items with id and quantity
 */
const getAddonItems = () => {
  return [...state.selectedAddons].map((id) => ({ id, quantity: 1 }));
};

/**
 * Add items to cart via Shopify API
 * @param {FormData|Object} data - FormData or { items: [...] }
 * @returns {Promise<Object>} Cart response
 */
const addToCart = async (data) => {
  const isJSON = !(data instanceof FormData);

  const options = {
    method: "POST",
    body: isJSON ? JSON.stringify(data) : data,
  };

  if (isJSON) {
    options.headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  const response = await fetch(CONFIG.endpoints.cartAdd, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || errorData.description || "Failed to add to cart"
    );
  }

  return response.json();
};